import { useEffect, useRef, useState } from "react";
import {
  Clock, ShieldCheck, HeartHandshake, Sparkles, Users2, Smartphone,
  ChevronLeft, ChevronRight, Fuel, Receipt, WifiOff,
} from "lucide-react";

/**
 * PainPointCarousel — full-bleed 6-slide hero on the login page.
 *
 * Voice: founder-first ("this happened to my pump, this is how HisabX solved it").
 * No fake numbers. Focus on: time saved, transparency, AI, audit, staff impact.
 * Style: editorial photojournalism + minimal illustrated stat slides.
 * Behaviour: auto-advance 6s, pause on hover, keyboard/arrow nav, dots + arrows.
 */

const SLIDES = [
  {
    id: "time-saved",
    theme: "dawn",     // warm amber / peach gradient
    kicker: "Time saved",
    headline: <>From 45 minutes<br/>to 8 minutes.</>,
    lede: "Shift close was our worst hour. Cashbook, HISAB, cash count, WhatsApp forwards to the manager — every night, 45 minutes minimum. HisabX made it 8. CSAs stopped hating the last hour of their shift. My manager stopped staying back past closing.",
    icon: Clock,
    art: "clock",
    stat: { big: "8 min", small: "avg. shift close" },
  },
  {
    id: "transparency",
    theme: "midnight", // deep slate with electric blue accent
    kicker: "Immutable audit",
    headline: <>Every reading.<br/>Every rupee.<br/>Every timestamp.</>,
    lede: "I don't remember 'trusting my paper HISAB' as a good feeling. Now every nozzle reading, cash line and credit entry has a name and a minute stamped on it. Nothing gets rewritten. Nothing goes missing. When something is off, we know exactly where — not who to blame.",
    icon: ShieldCheck,
    art: "nozzle",
    stat: { big: "0", small: "manual re-writes possible" },
  },
  {
    id: "staff-fairness",
    theme: "sage",     // muted green forest
    kicker: "Fair to staff",
    headline: <>My CSAs stopped losing sleep over ₹200.</>,
    lede: "The old ledger said A. The cash box said B. If it was short, salary was docked and nobody could explain why. Now the audit trail shows the exact litre and the exact minute. Fewer arguments, fewer resignations. Two of my CSAs told me they sleep better.",
    icon: HeartHandshake,
    art: "team",
    stat: { big: "Less friction", small: "with CSAs & manager" },
  },
  {
    id: "ai-brief",
    theme: "aurora",   // violet → cyan
    kicker: "Daily AI brief",
    headline: <>The AI catches what my accountant misses.</>,
    lede: "Every morning at 6 AM, HisabX WhatsApps a short summary — yesterday's sales, top nozzle, credit outstanding, unusual patterns. I read it with my chai before I leave home. I've caught leaking meters and slow-shortage patterns days before they became a problem.",
    icon: Sparkles,
    art: "brief",
    stat: { big: "6 AM", small: "morning briefing on WhatsApp" },
  },
  {
    id: "manager-trust",
    theme: "steel",    // brushed steel blue-grey
    kicker: "Two-step verify",
    headline: <>My manager runs the pump like it's his.</>,
    lede: "Every HISAB moves through the manager's verify. Rejection reasons are logged. Auto-approve only after 24 hours of silence. Now his work shows up on record — and mine backs off. He knows I'm watching without me being there.",
    icon: Users2,
    art: "verify",
    stat: { big: "24 h", small: "grace before auto-approve" },
  },
  {
    id: "runs-anywhere",
    theme: "monsoon",  // teal / indigo grid
    kicker: "6 languages · offline-first",
    headline: <>Hindi. Marathi. Gujarati.<br/>Tamil. Bengali. English.</>,
    lede: "On the CSA's ₹6,000 Android. Offline when the signal dies. Data locked to my pump. Auto-backed-up nightly. New hire onboarding used to take a week — now it's an hour. That is the difference language + intuitive design makes.",
    icon: Smartphone,
    art: "device",
    stat: { big: "0-bar friendly", small: "queues offline · syncs when back" },
  },
];

const THEMES = {
  dawn: {
    bg: "bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-rose-500/10 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-rose-950/40",
    accent: "text-amber-600 dark:text-amber-300",
    ring: "ring-amber-500/30",
    chip: "bg-amber-500/10 text-amber-700 dark:text-amber-200 border-amber-500/40",
    grid: "bg-[radial-gradient(circle_at_20%_20%,rgba(251,146,60,0.14),transparent_50%)]",
  },
  midnight: {
    bg: "bg-gradient-to-br from-slate-900 via-slate-950 to-black text-white dark:from-slate-900",
    accent: "text-sky-300",
    ring: "ring-sky-400/40",
    chip: "bg-sky-500/15 text-sky-200 border-sky-400/40",
    grid: "bg-[radial-gradient(circle_at_80%_30%,rgba(56,189,248,0.20),transparent_55%)]",
  },
  sage: {
    bg: "bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-lime-500/10 dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-lime-950/40",
    accent: "text-emerald-600 dark:text-emerald-300",
    ring: "ring-emerald-500/30",
    chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 border-emerald-500/40",
    grid: "bg-[radial-gradient(circle_at_75%_70%,rgba(16,185,129,0.15),transparent_50%)]",
  },
  aurora: {
    bg: "bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-cyan-500/10 dark:from-violet-950/50 dark:via-fuchsia-950/30 dark:to-cyan-950/40",
    accent: "text-fuchsia-600 dark:text-fuchsia-300",
    ring: "ring-fuchsia-500/30",
    chip: "bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-200 border-fuchsia-500/40",
    grid: "bg-[radial-gradient(circle_at_50%_100%,rgba(217,70,239,0.18),transparent_55%)]",
  },
  steel: {
    bg: "bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white",
    accent: "text-cyan-300",
    ring: "ring-cyan-400/40",
    chip: "bg-cyan-500/15 text-cyan-100 border-cyan-400/40",
    grid: "bg-[radial-gradient(circle_at_25%_80%,rgba(34,211,238,0.15),transparent_55%)]",
  },
  monsoon: {
    bg: "bg-gradient-to-br from-teal-500/10 via-indigo-500/5 to-blue-500/10 dark:from-teal-950/40 dark:via-indigo-950/30 dark:to-blue-950/40",
    accent: "text-teal-600 dark:text-teal-300",
    ring: "ring-teal-500/30",
    chip: "bg-teal-500/10 text-teal-700 dark:text-teal-200 border-teal-500/40",
    grid: "bg-[radial-gradient(circle_at_10%_50%,rgba(20,184,166,0.16),transparent_55%)]",
  },
};


export default function PainPointCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef(null);

  // Auto-advance every 6s unless the user hovers or focuses inside.
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  // Keyboard arrow support when the carousel has focus.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % SLIDES.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, []);

  const go = (n) => setIdx(((n % SLIDES.length) + SLIDES.length) % SLIDES.length);

  return (
    <section
      ref={rootRef}
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Founder stories — from pain to solved"
      className="relative w-full overflow-hidden border-y border-border/50 outline-none focus:ring-2 focus:ring-primary/40"
      data-testid="pain-point-carousel"
    >
      {/* Section pre-title */}
      <div className="border-b border-border/50 bg-card/40 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
            <span className="inline-block h-2 w-2 rounded-full bg-primary" />
            Founder stories · from my pump to yours
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
          </div>
        </div>
      </div>

      {/* Slide stage */}
      <div className="relative min-h-[520px] w-full sm:min-h-[560px]">
        {SLIDES.map((slide, i) => (
          <Slide
            key={slide.id}
            slide={slide}
            active={i === idx}
            testid={`carousel-slide-${slide.id}`}
          />
        ))}

        {/* Arrows */}
        <button
          type="button"
          onClick={() => go(idx - 1)}
          aria-label="Previous story"
          className="absolute left-3 top-1/2 z-30 -translate-y-1/2 rounded-full border border-border/60 bg-background/70 p-2 backdrop-blur transition hover:-translate-y-1/2 hover:scale-105 hover:bg-background md:left-6 md:p-3"
          data-testid="carousel-prev"
        >
          <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
        </button>
        <button
          type="button"
          onClick={() => go(idx + 1)}
          aria-label="Next story"
          className="absolute right-3 top-1/2 z-30 -translate-y-1/2 rounded-full border border-border/60 bg-background/70 p-2 backdrop-blur transition hover:-translate-y-1/2 hover:scale-105 hover:bg-background md:right-6 md:p-3"
          data-testid="carousel-next"
        >
          <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
        </button>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 border-t border-border/50 bg-card/40 py-4 backdrop-blur">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`Go to story ${i + 1}: ${s.kicker}`}
            className={
              "h-1.5 rounded-full transition-all " +
              (i === idx ? "w-8 bg-primary" : "w-2 bg-border hover:bg-muted-foreground/40")
            }
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}


function Slide({ slide, active, testid }) {
  const theme = THEMES[slide.theme] || THEMES.dawn;
  const Icon = slide.icon;
  const isDark = slide.theme === "midnight" || slide.theme === "steel";
  return (
    <div
      className={
        "absolute inset-0 transition-opacity duration-700 " +
        (active ? "z-10 opacity-100" : "pointer-events-none z-0 opacity-0")
      }
      aria-hidden={!active}
      data-testid={testid}
    >
      <div className={"absolute inset-0 " + theme.bg}>
        {/* Ambient grid overlay */}
        <div className={"absolute inset-0 " + theme.grid} aria-hidden />
        {/* Subtle noise/dot pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          aria-hidden
          style={{
            backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
            backgroundSize: "18px 18px",
          }}
        />
      </div>

      <div className="relative mx-auto grid h-full max-w-7xl grid-cols-1 items-center gap-8 px-6 py-14 md:grid-cols-12 md:gap-12 md:px-10 md:py-16">
        {/* Left: copy */}
        <div className={"col-span-1 md:col-span-7 " + (active ? "animate-slide-in" : "")}>
          <div className={"inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] " + theme.chip}>
            <Icon className="h-3.5 w-3.5" />
            {slide.kicker}
          </div>

          <h2
            className={
              "mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl " +
              (isDark ? "text-white" : "text-foreground")
            }
          >
            {slide.headline}
          </h2>

          <p
            className={
              "mt-5 max-w-2xl text-[15px] leading-relaxed sm:text-base " +
              (isDark ? "text-white/75" : "text-muted-foreground")
            }
          >
            {slide.lede}
          </p>

          {/* Founder attribution */}
          <div className="mt-6 flex items-center gap-3">
            <div className={"flex h-9 w-9 items-center justify-center rounded-full font-bold ring-2 " + theme.ring + " " + (isDark ? "bg-white/10 text-white" : "bg-primary/15 " + theme.accent)}>
              F
            </div>
            <div className="text-xs">
              <div className={"font-semibold " + (isDark ? "text-white" : "text-foreground")}>Founder · HisabX</div>
              <div className={isDark ? "text-white/60" : "text-muted-foreground"}>Built at my own pump. Now used at yours.</div>
            </div>
          </div>
        </div>

        {/* Right: illustration + stat */}
        <div className="col-span-1 md:col-span-5">
          <SlideArt kind={slide.art} theme={theme} isDark={isDark} />
          <div className={"mt-5 rounded-2xl border p-5 backdrop-blur " + (isDark ? "border-white/10 bg-white/5" : "border-border/60 bg-background/50")}>
            <div className={"font-display text-4xl font-bold leading-none tracking-tight sm:text-5xl " + theme.accent}>
              {slide.stat.big}
            </div>
            <div className={"mt-2 text-xs uppercase tracking-widest " + (isDark ? "text-white/60" : "text-muted-foreground")}>
              {slide.stat.small}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * SlideArt — dispatches to a per-slide inline SVG illustration. Everything
 * is drawn as SVG so nothing depends on network image fetches.
 */
function SlideArt({ kind, theme, isDark }) {
  const stroke = isDark ? "rgba(255,255,255,0.85)" : "currentColor";
  const soft = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const container =
    "relative aspect-[4/3] w-full overflow-hidden rounded-2xl border " +
    (isDark ? "border-white/10 bg-white/5" : "border-border/60 bg-card/60") +
    " shadow-inner";

  if (kind === "clock") {
    return (
      <div className={container} aria-hidden>
        <svg viewBox="0 0 400 300" className="h-full w-full">
          <defs>
            <linearGradient id="dial" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#f97316" stopOpacity="0.85" />
              <stop offset="1" stopColor="#f43f5e" stopOpacity="0.75" />
            </linearGradient>
          </defs>
          {/* Old dial (crossed out) */}
          <g transform="translate(90,150)">
            <circle r="70" fill="url(#dial)" opacity="0.25" />
            <circle r="70" fill="none" stroke={stroke} strokeWidth="2" opacity="0.4" />
            <text x="0" y="-6" textAnchor="middle" fontSize="34" fontWeight="800" fill={stroke} opacity="0.85">45</text>
            <text x="0" y="20" textAnchor="middle" fontSize="12" fontWeight="600" fill={stroke} opacity="0.55">MINUTES</text>
            <line x1="-88" y1="88" x2="88" y2="-88" stroke="#ef4444" strokeWidth="4" strokeLinecap="round"/>
          </g>
          {/* Arrow */}
          <g stroke={stroke} strokeWidth="2.5" fill="none" opacity="0.6">
            <path d="M175 150 L235 150" strokeLinecap="round"/>
            <path d="M225 140 L235 150 L225 160" strokeLinecap="round"/>
          </g>
          {/* New dial */}
          <g transform="translate(310,150)">
            <circle r="70" fill="url(#dial)" opacity="0.95" />
            <circle r="70" fill="none" stroke={stroke} strokeWidth="2" opacity="0.9" />
            <text x="0" y="-2" textAnchor="middle" fontSize="42" fontWeight="800" fill={isDark ? "#fff" : "#0f172a"}>8</text>
            <text x="0" y="22" textAnchor="middle" fontSize="12" fontWeight="700" fill={isDark ? "#fff" : "#0f172a"} opacity="0.75">MIN</text>
          </g>
        </svg>
      </div>
    );
  }

  if (kind === "nozzle") {
    return (
      <div className={container} aria-hidden>
        <svg viewBox="0 0 400 300" className="h-full w-full">
          {/* Forecourt floor */}
          <rect x="0" y="220" width="400" height="80" fill={soft} />
          {/* Pump body */}
          <g transform="translate(140,60)">
            <rect x="0" y="0" width="120" height="160" rx="12" fill="url(#pumpGrad)" />
            <rect x="12" y="16" width="96" height="60" rx="8" fill={isDark ? "#0ea5e9" : "#0f172a"} />
            <text x="60" y="42" textAnchor="middle" fontSize="14" fontWeight="700" fill="#7dd3fc">₹ 12,458.00</text>
            <text x="60" y="62" textAnchor="middle" fontSize="10" fill="#38bdf8">124.58 L</text>
            <rect x="12" y="90" width="96" height="8" rx="3" fill={stroke} opacity="0.25"/>
            <rect x="12" y="105" width="70" height="6" rx="2" fill={stroke} opacity="0.15"/>
            <rect x="12" y="118" width="55" height="6" rx="2" fill={stroke} opacity="0.15"/>
            <circle cx="60" cy="145" r="6" fill="#22c55e" />
          </g>
          <defs>
            <linearGradient id="pumpGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={isDark ? "#1e293b" : "#e2e8f0"} />
              <stop offset="1" stopColor={isDark ? "#0f172a" : "#cbd5e1"} />
            </linearGradient>
          </defs>
          {/* Hose */}
          <path d="M260 90 C 310 90 320 150 340 150" fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round" opacity="0.55"/>
          {/* Timestamp ticks */}
          <g fontSize="10" fill={stroke} opacity="0.6" fontFamily="ui-monospace,monospace">
            <text x="30" y="40">06:12 · CSA A · N1 · 12,458.00</text>
            <text x="30" y="58">06:18 · CSA A · N2 · 4,392.10</text>
            <text x="30" y="76">14:05 · CSA B · N1 · 15,881.40</text>
          </g>
        </svg>
      </div>
    );
  }

  if (kind === "team") {
    return (
      <div className={container} aria-hidden>
        <svg viewBox="0 0 400 300" className="h-full w-full">
          {/* Three CSA avatars in uniforms */}
          {[{ x: 90, hue: "#f59e0b" }, { x: 200, hue: "#10b981" }, { x: 310, hue: "#3b82f6" }].map((p, i) => (
            <g key={i} transform={`translate(${p.x},130)`}>
              <circle r="40" fill={p.hue} opacity="0.18" />
              <circle cx="0" cy="-8" r="16" fill={p.hue} />
              <path d="M-24 30 C -20 5 20 5 24 30 Z" fill={p.hue} />
              <rect x="-10" y="10" width="20" height="8" fill={isDark ? "#0f172a" : "#fff"} opacity="0.6"/>
            </g>
          ))}
          {/* Speech bubble */}
          <g transform="translate(60,40)">
            <rect x="0" y="0" width="280" height="52" rx="14" fill={isDark ? "#0f172a" : "#fff"} stroke={stroke} strokeWidth="1.5" opacity="0.9"/>
            <text x="16" y="22" fontSize="12" fontWeight="700" fill={stroke}>Salary docked ₹200. Nobody knew why.</text>
            <text x="16" y="40" fontSize="11" fill={stroke} opacity="0.7">Now the audit trail shows the exact litre.</text>
          </g>
        </svg>
      </div>
    );
  }

  if (kind === "brief") {
    return (
      <div className={container} aria-hidden>
        <svg viewBox="0 0 400 300" className="h-full w-full">
          {/* Phone frame */}
          <g transform="translate(120,25)">
            <rect x="0" y="0" width="160" height="260" rx="20" fill={isDark ? "#0f172a" : "#111827"} />
            <rect x="8" y="12" width="144" height="236" rx="12" fill={isDark ? "#020617" : "#f8fafc"} />
            {/* WhatsApp-style card */}
            <g transform="translate(16,28)">
              <rect x="0" y="0" width="128" height="72" rx="10" fill="#25D366" opacity="0.9"/>
              <circle cx="14" cy="14" r="8" fill="#fff" />
              <text x="26" y="17" fontSize="8" fontWeight="700" fill="#fff">HisabX AI</text>
              <text x="10" y="36" fontSize="7" fill="#e0f2fe">Today's briefing</text>
              <text x="10" y="48" fontSize="7" fill="#e0f2fe">Sales: ₹4.2L</text>
              <text x="10" y="60" fontSize="7" fill="#e0f2fe">⚠ Nozzle 3 slow drift</text>
            </g>
            <g transform="translate(16,116)">
              <rect x="0" y="0" width="128" height="52" rx="10" fill={isDark ? "#1e293b" : "#e2e8f0"}/>
              <text x="10" y="18" fontSize="7" fontWeight="700" fill={stroke}>Top nozzle</text>
              <text x="10" y="32" fontSize="10" fontWeight="700" fill={stroke}>M2 · N1 (Diesel)</text>
              <text x="10" y="45" fontSize="6" fill={stroke} opacity="0.65">1,254 L · ₹1.12L</text>
            </g>
            <g transform="translate(16,180)">
              <rect x="0" y="0" width="128" height="52" rx="10" fill={isDark ? "#1e293b" : "#e2e8f0"}/>
              <text x="10" y="18" fontSize="7" fontWeight="700" fill={stroke}>Credit outstanding</text>
              <text x="10" y="32" fontSize="10" fontWeight="700" fill={stroke}>₹18,240</text>
              <text x="10" y="45" fontSize="6" fill={stroke} opacity="0.65">4 customers · 2 overdue</text>
            </g>
          </g>
        </svg>
      </div>
    );
  }

  if (kind === "verify") {
    return (
      <div className={container} aria-hidden>
        <svg viewBox="0 0 400 300" className="h-full w-full">
          {/* Flow: CSA → Manager → Dealer */}
          {[
            { x: 70,  label: "CSA close",   sub: "submits HISAB",  color: "#f59e0b" },
            { x: 200, label: "Manager",     sub: "verifies · logs", color: "#10b981" },
            { x: 330, label: "Dealer",      sub: "reads on WhatsApp", color: "#3b82f6" },
          ].map((n, i) => (
            <g key={i} transform={`translate(${n.x},150)`}>
              <circle r="46" fill={n.color} opacity="0.18" />
              <circle r="30" fill={n.color} />
              <text y="4" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{n.label}</text>
              <text y="72" textAnchor="middle" fontSize="10" fontWeight="600" fill={stroke}>{n.sub}</text>
            </g>
          ))}
          {/* Arrows between */}
          {[[116, 154],[246, 284]].map(([x1, x2], i) => (
            <g key={i} stroke={stroke} strokeWidth="2" fill="none" opacity="0.55">
              <path d={`M${x1} 150 L${x2} 150`} strokeLinecap="round"/>
              <path d={`M${x2-8} 143 L${x2} 150 L${x2-8} 157`} strokeLinecap="round"/>
            </g>
          ))}
          {/* Audit ticker at top */}
          <g fontSize="9" fill={stroke} opacity="0.55" fontFamily="ui-monospace,monospace">
            <text x="30" y="30">14:32 · verified by manager</text>
            <text x="30" y="46">14:33 · alert sent to dealer</text>
          </g>
        </svg>
      </div>
    );
  }

  // device (default: languages + offline chip)
  return (
    <div className={container} aria-hidden>
      <svg viewBox="0 0 400 300" className="h-full w-full">
        {/* Phone */}
        <g transform="translate(140,25)">
          <rect x="0" y="0" width="120" height="250" rx="18" fill={isDark ? "#020617" : "#0f172a"} />
          <rect x="7" y="10" width="106" height="230" rx="10" fill={isDark ? "#0f172a" : "#f1f5f9"} />
          {/* Language chips */}
          {[
            { t: "हिन्दी", y: 30 }, { t: "मराठी", y: 60 }, { t: "ગુજરાતી", y: 90 },
            { t: "தமிழ்", y: 120 }, { t: "বাংলা", y: 150 }, { t: "English", y: 180 },
          ].map((c, i) => (
            <g key={i} transform={`translate(15,${c.y})`}>
              <rect x="0" y="0" width="90" height="22" rx="11" fill={isDark ? "#1e293b" : "#e2e8f0"} />
              <text x="45" y="15" textAnchor="middle" fontSize="10" fontWeight="700" fill={stroke}>{c.t}</text>
            </g>
          ))}
          {/* Offline badge */}
          <g transform="translate(20,208)">
            <rect x="0" y="0" width="80" height="20" rx="10" fill="#f59e0b" opacity="0.15" stroke="#f59e0b" strokeWidth="1"/>
            <text x="40" y="14" textAnchor="middle" fontSize="8" fontWeight="800" fill="#b45309">OFFLINE READY</text>
          </g>
        </g>
        {/* Signal bars muted */}
        <g transform="translate(50,140)">
          <rect x="0"  y="30" width="6" height="8"  fill={stroke} opacity="0.3"/>
          <rect x="10" y="24" width="6" height="14" fill={stroke} opacity="0.25"/>
          <rect x="20" y="16" width="6" height="22" fill={stroke} opacity="0.15"/>
          <rect x="30" y="8"  width="6" height="30" fill={stroke} opacity="0.1"/>
          <line x1="-4" y1="-4" x2="40" y2="44" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
        </g>
        {/* Sync arrow */}
        <g transform="translate(310,140)" stroke={stroke} strokeWidth="2.5" fill="none" opacity="0.6">
          <path d="M 0 0 A 20 20 0 1 1 -0.001 0" strokeLinecap="round"/>
          <path d="M 0 0 L 8 -6 L 4 -14" strokeLinecap="round"/>
        </g>
      </svg>
    </div>
  );
}
